import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendUp, TrendDown, ArrowSquareOut } from "@phosphor-icons/react";
import IndexDetailModal from "./IndexDetailModal";
import { tradingViewIndexUrl } from "../lib/tradingview";
import { BACKEND_BASE_URL } from "../lib/api";

// Public endpoint — no auth cookies needed (uses plain axios to avoid interceptor
// unnecessarily attaching Authorization header)
const publicApi = axios.create({ baseURL: `${BACKEND_BASE_URL}/api` });

const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    : "—";

export default function IndexStrip({ compact = false }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const { data } = await publicApi.get("/indices");
      setData(data);
    } catch (e) {
      console.error("/indices load", e);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60 * 1000); // refresh every minute
    return () => clearInterval(id);
  }, []);

  const items = data?.indices || [];
  if (!items.length) {
    return (
      <div
        className={
          "border-b border-subtle bg-app/70 " + (compact ? "py-1.5 px-4" : "py-2 px-6")
        }
        data-testid="index-strip-empty"
      >
        <div className="flex items-center gap-6 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          <span className="animate-pulse">Loading indices…</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        "relative border-y border-subtle bg-app/80 backdrop-blur-sm " +
        (compact ? "py-1.5 px-4" : "py-2 px-6")
      }
      data-testid="index-strip"
    >
      <div className="flex items-center gap-4 md:gap-8 overflow-x-auto no-scrollbar">
        <span className="shrink-0 text-[9px] font-mono uppercase tracking-[0.25em] text-muted-fg">
          Markets
        </span>
        {items.map((ix) => {
          const pos = ix.change_pct >= 0;
          return (
            <div
              key={ix.code}
              className="shrink-0 flex items-center gap-1 font-mono text-xs hover:bg-surface-hover px-2 -mx-2 py-0.5 rounded-sm transition"
            >
              <button
                type="button"
                onClick={() => setSelected(ix)}
                data-testid={`index-${ix.code}`}
                className="flex items-center gap-2 cursor-pointer"
                title="Click for detailed chart"
              >
                <span className="text-primary-fg tracking-wider font-medium">{ix.name}</span>
                <span className="text-primary-fg tabular-nums">{fmt(ix.price)}</span>
                <span
                  className={
                    "flex items-center gap-0.5 tabular-nums " + (pos ? "text-pos" : "text-neg")
                  }
                >
                  {pos ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
                  {pos ? "+" : ""}
                  {ix.change_pct.toFixed(2)}%
                </span>
              </button>
              <a
                href={tradingViewIndexUrl(ix.code)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`index-tv-${ix.code}`}
                title={`Open ${ix.name} on TradingView`}
                className="text-muted-fg hover:text-pos transition ml-0.5 p-0.5"
              >
                <ArrowSquareOut size={11} weight="bold" />
              </a>
              <span className="text-muted-fg hidden md:inline">·</span>
            </div>
          );
        })}
        <span className="ml-auto shrink-0 text-[9px] font-mono uppercase tracking-widest text-muted-fg hidden md:inline">
          live · click name for chart · ↗ opens TradingView
        </span>
      </div>
      <IndexDetailModal index={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
